<script lang="ts">
  import { logger } from "$lib/utils/logger";
  import type { ContentApiResponse } from "$lib/elev8Api/contentTypes";
  import {
    fetchAndStageNextContent,
    getLatestContent,
    shouldSwapContent,
  } from "$lib/elev8Api/contentFetch";
  import SpotDevOverlay from "$lib/spot/SpotDevOverlay.svelte";
  import ManualSpotController from "./ManualSpotController.svelte";
  import SpotRenderer from "$lib/spot/SpotRenderer.svelte";
  import LoadingContent from "$lib/content/components/LoadingContent.svelte";

  interface Props {
    displayId: string;
    devOverlay?: boolean;
  }
  let { displayId, devOverlay = false }: Props = $props();

  let spots: ContentApiResponse["spots"] = $state.raw([]);
  let latestContentPromise = $derived(getLatestContent(displayId));
  $effect(() => {
    latestContentPromise
      .then((response) => {
        spots = response.spots || [];
      })
      .catch(() => {}); // prevent unhandled rejection — errors are handled by {:catch} in the template
  });
  let spotIndex = $state(0);
  let spot = $derived(spots[spotIndex]);
  let spotDuration = $derived(spot?.duration || 10);
  let isPaused = $state(false);
  let spotTimeRemaining = $derived(spotDuration);

  let addTime = $state(0); // buffer time to add, written by addBufferTime, read by interval
  export function addBufferTime(seconds: number) {
    if (seconds <= 0 || !Number.isFinite(seconds)) return;
    addTime += Math.ceil(seconds);
  }

  // Timer — restarts whenever spotIndex changes. Coarse 250ms tick: a high-frequency
  // timer here pins the WPE WebKit main thread and grows memory over uptime, so keep
  // this interval coarse. performance.now() keeps the countdown drift-resistant.
  $effect(() => {
    void spotIndex; // read spotIndex to establish the dependency
    addTime = 0;
    const duration = spotDuration; // read duration once at effect creation
    let timeRemaining = duration;
    let lastTick = performance.now();
    spotTimeRemaining = timeRemaining;

    function tick() {
      const now = performance.now();
      if (isPaused) {
        lastTick = now;
        return;
      }
      if (addTime > 0) {
        timeRemaining += addTime;
        addTime = 0;
      }
      const elapsed = Math.floor((now - lastTick) / 1000);
      if (elapsed >= 1) {
        timeRemaining -= elapsed;
        lastTick += elapsed * 1000; // adjust lastTick forward by the elapsed whole seconds to prevent drift (vs `= now`)
        spotTimeRemaining = Math.max(timeRemaining, 0);
      }
      if (timeRemaining <= 0) {
        nextSpot();
      }
    }
    const intervalId = setInterval(tick, 250);

    return () => clearInterval(intervalId);
  });

  function nextSpot() {
    logger.debug("[SpotController] Next spot requested");
    spotIndex++;
    if (shouldSwapContent() || spotsArrayIsEmpty(spots.length, spotIndex)) {
      logger.debug("[SpotController] Loading latest content and restarting.");
      spotIndex = 0;
      latestContentPromise = getLatestContent(displayId); // assign the promise, not the result. i.e. don't await here, {#await}s in the template
    }
    if (spotsAlmostEmpty(spots.length, spotIndex)) {
      logger.info(
        "[SpotController] Spots array is almost empty, fetching next content"
      );
      fetchAndStageNextContent(displayId).catch((error) => {
        logger.error("[SpotController] Error fetching next content:", error);
      });
    }
    logger.debug(
      `[SpotController] Spot index: ${spotIndex}, duration: ${spotDuration}s`
    );
  }

  function spotsAlmostEmpty(spotsLength: number, spotIndex: number) {
    const threshold = 2;
    return spotsLength - spotIndex <= threshold;
  }

  function spotsArrayIsEmpty(spotsLength: number, spotIndex: number) {
    return spotsLength === 0 || spotIndex >= spotsLength;
  }
</script>

{#await latestContentPromise}
  <LoadingContent {displayId} />
{:then contentResponse}
  <SpotRenderer {spotIndex} {...contentResponse} {addBufferTime} />

  <ManualSpotController
    next={nextSpot}
    spotArrayLength={spots.length}
    {spotIndex}
    {spotTimeRemaining}
    {spotDuration}
    bind:isPaused
    bind:devOverlay
  >
    <SpotDevOverlay {spot} />
  </ManualSpotController>
{:catch}
  <LoadingContent {displayId} />
{/await}
